'use client';

// The change queue counts' bridge (BL-2438 AC 4, BL-2569). The phone app's offline bar is mounted
// above the signed-in shell (RealtimeProviderMount), but the change queue is read inside it
// (OfflineSyncProvider, which needs the tenant and user). The provider reports its counts up here
// from one read: the bar shows the not-synced count, and the sidebar's Storage & Sync entry shows,
// in red, the count of changes that need someone.

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/** The queue's two counts, from one read: rows not yet synced, and rows that need someone (BL-2569). */
interface QueueCounts {
  notSynced: number;
  issues: number;
}

interface BridgeValue {
  counts: QueueCounts | null;
  setCounts: (counts: QueueCounts | null) => void;
}

const ChangeQueueCountContext = createContext<BridgeValue | null>(null);

/**
 * Holds the change queue's not-synced count for surfaces mounted above the signed-in shell.
 *
 * @param props.children - the bar and the app.
 * @returns the provider.
 */
export function ChangeQueueCountBridge({ children }: { children: ReactNode }) {
  const [counts, setCounts] = useState<QueueCounts | null>(null);
  const value = useMemo(() => ({ counts, setCounts }), [counts]);
  return <ChangeQueueCountContext.Provider value={value}>{children}</ChangeQueueCountContext.Provider>;
}

/**
 * @returns the not-synced count (rows as the Change queue tab shows them), or null when no queue
 *   is open (outside the native app, before the store opens, or with no bridge mounted).
 */
export function useChangeQueueNotSyncedCount(): number | null {
  return useContext(ChangeQueueCountContext)?.counts?.notSynced ?? null;
}

/**
 * @returns the count of changes that need someone to act (a failed or refused row, and each failed
 *   photo under a bucket, BL-2569), or null when no queue is open.
 */
export function useChangeQueueIssueCount(): number | null {
  return useContext(ChangeQueueCountContext)?.counts?.issues ?? null;
}

/**
 * Report the queue's counts to the bridge; clears them when the reporter unmounts (sign-out, a
 * tenant switch), so no surface keeps a previous session's number.
 *
 * @param counts - both counts, or null while the queue is not read.
 * @sideEffect Writes the bridge's counts.
 */
export function useReportChangeQueueCounts(counts: QueueCounts | null): void {
  const setCounts = useContext(ChangeQueueCountContext)?.setCounts;
  const notSynced = counts?.notSynced ?? null;
  const issues = counts?.issues ?? null;
  useEffect(() => {
    setCounts?.(notSynced == null || issues == null ? null : { notSynced, issues });
  }, [setCounts, notSynced, issues]);
  useEffect(() => () => setCounts?.(null), [setCounts]);
}
